/*
 *
 * Copyright 2021-2024, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import './WrcListAndDetail.css';
import React from 'react';
import ReactDOM from "react-dom";
import { Button } from "@blueprintjs/core";
import { Card, Elevation } from "@blueprintjs/core";
import { Callout, Collapse } from "@blueprintjs/core";
import { Intent } from "@blueprintjs/core/lib/esm/index";
import { IconNames } from "@blueprintjs/icons";
import { Classes, Tooltip2 } from "@blueprintjs/popover2";
import { WrcCardHeader } from "./WrcCardHeader";
import { WrcForm } from "./WrcForm.js"
import { WrcRecordDetail } from "./WrcRecordDetail.js"
import { WrcScheduleOpener } from "./WrcScheduleOpener.js"
import { WrcServer } from "./WrcServer.js";
import { WrcDataTable } from "./WrcDataTables.js"
import { WrcStatusDashboard } from './WrcStatusDashboard.js';

export class WrcListAndDetail extends React.Component {
    constructor(props) {
        super(props);
        // pass this.props.program to children then check the children JSON for "read-only-if"
        // add the read only if bit to the "children" of patient component
        this.state = {
            is_loaded: false,
            readonly_if: this.checkReadOnlyIf(this.props),
            show_list: (this.props.initially_enabled !== undefined ? this.props.initially_enabled : true),
            collapse_enabled: (this.props.enable_minimization !== undefined ? this.props.enable_minimization : false),
            selected_item: null,
            list_data: [],
            record_id: null
        };
    }

    componentDidMount() {
        // can scrape parentGetState for values here instead of multiple database calls every refresh. list_data is the heavy one so check for that first
        let parentState = this.props.parentGetState()
        if (parentState[this.props.entity] === undefined || parentState[this.props.entity]['list_data'] === undefined) {
            let post = { table_schema: this.props.table_schema, table_name: this.props.table_name };
            let additional_columns = this.props.columns.filter((column) => column.formula !== undefined)
            if (additional_columns.length > 0) {
                post.additional_columns = additional_columns;
            }
            WrcServer.post('get-records',
                post,
                (content) => this.getData(content)
            )
        } else {
            this.setState({
                list_data: parentState[this.props.entity]['list_data'],
                selected_item: parentState[this.props.entity]['selected_item'] !== undefined ? parentState[this.props.entity]['selected_item'] : null,
                record_id: parentState[this.props.entity]['record_id'] !== undefined ? parentState[this.props.entity]['record_id'] : null,
                is_loaded: true
            });
        }
    }

    getData(content) {
        const newState = {
            is_loaded: true,
            list_data: content
        }
        const newRecord = {}
        if (!isNaN(this.state.record_id)) {
            content.find((o, i) => {
                if (o.id == this.state.record_id) {
                    newRecord = {
                        record_id: this.state.list_data[i]['id'],
                        selected_item: i,
                        current_tab: 'tab1'
                    }
                    return true;
                }
                return false
            })
        }
        this.setState({ ...newState, ...newRecord })
    }

    // put things into parent component state for retrieval on refresh
    componentWillUnmount() {
        this.props.parentSetState({
            [this.props.entity]: {
                list_data: this.state.list_data,
                record_id: this.state.record_id,
                selected_item: this.state.selected_item
            }
        })
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.selected_item !== this.state.selected_item) {
            if (this.state.selected_item !== null) {
                document.getElementById('child-' + this.props.component_index).scrollIntoView(false)
            } 
        }
    }

    checkReadOnlyIf(context) {
        if (context.readonly_if === undefined || context.readonly_if === null) {
            return false;
        }
        let field = context[context.readonly_if.field_name]
        let is_readonly = context.readonly_if.values.every(function(value) {
            if (value === '*') {
                return (field !== undefined ||
                        field !== null ||
                        field !== '' ||
                        (Array.isArray(field) && field.length > 0)
                );
            }
            if (value === 'none') {
                return (field === undefined);
            }
            if (Array.isArray(field)){
                return (!field.includes(value));
            } else {
                return (value === field);
            }
        });
        return is_readonly
    }

    showItemClick(e, row, source) {
        if (this.state.selected_item === row) {
            if (source != 'getData') {
                this.setState({
                    record_id: null,
                    selected_item: null,
                    current_tab: 'tab1'
                });
            }
        } else {
            this.setState({
                record_id: this.state.list_data[row]['id'],
                selected_item: row,
                current_tab: 'tab1'
            })

        }
    }

    // passed to child components so data can be added into the appropriate state table when a record is added/updated so a DB call to refresh isn't required
    parentCallback = (data) => {
        let index = 0;
        let combined = {};
        index = this.state.list_data.findIndex((row) => row.id === data.id);
        if (index === -1) {
            this.state.list_data.push(data)
        } else {
            combined = { ...this.state.list_data[index], ...data }
            this.state.list_data[index] = combined
        }
    }

    showForm() {
        ReactDOM.render(
            <WrcForm
                form_id={this.props.form_id}
                refresh={this.props.refresh}
                parentCallback={this.parentCallback}
                component_index={this.props.component_index} />,
            document.querySelector('#form'));
    }

    handleListShow = () => {
        this.setState({ show_list: !this.state.show_list });
    }

    render() {
        // variables that need to be defined for final return JSX
        let ld_full_class = this.props.app_layout.main_layout === 'vertical' ? 'app-list-and-detail-v' : 'app-list-and-detail-h';
        let ld_list_class = this.props.app_layout.main_layout === 'vertical' ? 'app-LD-list-v' : 'app-LD-list-h';
        let ld_detail_class = this.props.app_layout.main_layout === 'vertical' ? 'app-LD-detail-v' : 'app-LD-detail-h';
        let ld_card_alignment = this.props.component_alignment === 'center' ? 'center' : 'left';
        ld_full_class += " ";
        ld_full_class += ld_card_alignment;
        let list_headers = this.props.columns.map(function (column) {
            let column_class = column.class_name || '';
            let column_style = typeof (column.style) === 'object' ? column.style : {};
            if (column.show_in_list === false) {
                return '';
            } else {
                return (<th className={column_class} style={column_style}>{column.header}</th>);
            }
        }.bind(this));
        let local_list_data = [];
        if (this.state.is_loaded) {
            local_list_data = this.state.list_data.map(function (record, index) {
                let fields = this.props.columns.map(function (column) {
                    let column_class = column.class_name || '';
                    let column_style = typeof (column.style) === 'object' ? column.style : {};
                    if (column.show_in_list === false) {
                        return '';
                    } else {
                        let display_value = record[column.field];
                        if (display_value && column.element_type === 'datestamp') {
                            display_value = (
                                <Tooltip2 className={Classes.TOOLTIP2_INDICATOR} content={display_value} intent={Intent.PRIMARY} placement="right">
                                    {display_value.substring(0, 10)}
                                </Tooltip2>);
                        }
                        if (display_value === true) display_value = 'Yes';
                        if (display_value === false) display_value = 'No';            
                        return (<td className={column_class} style={column_style}>{display_value}</td>);
                    }
                }.bind(this));
                return (
                    <tr>
                        {fields}
                        <td style={{ textAlign: 'center' }}>
                            <Button icon={this.state.selected_item === index ? IconNames.FOLDER_OPEN : IconNames.FOLDER_CLOSE}
                                intent={Intent.NONE}
                                minimal={true}
                                active={this.state.selected_item === index}
                                onClick={(e) => this.showItemClick(e, index, 'button')} />
                        </td>
                    </tr>);
            }.bind(this));
        }
        let details = (<div></div>);
        if (this.state.selected_item === null) {
            details = (
                <div class="app-record-detail-children-h">
                    <Card interactive={false} elevation={Elevation.ZERO}>
                        <Callout intent={Intent.WARNING} title='No record selected' style={{ minWidth: 500 }}>Click a folder in the {this.props.entity} List to view record details.</Callout>
                    </Card>
                </div>
            );
        } else {
            details = (
                <WrcRecordDetail
                    app_layout={this.props.app_layout}
                    component_alignment={this.props.component_alignment}
                    component_index={this.props.component_index}
                    refresh={this.props.refresh}
                    parentSetState={this.props.parentSetState}
                    parentGetState={this.props.parentGetState}
                    parentCallback={this.parentCallback}
                    entity={this.props.entity}
                    form_id={this.props.form_id}
                    table_schema={this.props.table_schema}
                    table_name={this.props.table_name}
                    columns={this.props.columns}
                    detail_buttons={this.props.detail_buttons}
                    children={this.props.children}
                    local_id={this.props.local_id}
                    page_length={this.props.page_length}
                    class_name={this.props.class_name}
                    hide_edit={this.props.hide_edit}
                    hide_delete={this.props.hide_delete || this.props.readonly}
                    readonly={this.props.readonly}
                    user={this.props.user}

                    program_role={this.props.program_role}
                    summary_readonly_if_check={this.state.readonly_if}
                    checkReadOnlyIf={this.checkReadOnlyIf}

                    record_id={this.state.record_id}
                    selected_item={this.state.selected_item}
                    list_data={this.state.list_data}
                />
            );
        }

        let dashboard = (<div></div>);
        if (this.props.db_schema === undefined) {
            dashboard = "";
        } else if (this.state.selected_item === null) {
            dashboard = (
                <div className={ld_detail_class}>
                    <div class="app-record-detail-children-h">
                        <Card interactive={false} elevation={Elevation.ZERO}>
                            <Callout intent={Intent.WARNING} title='No record selected' style={{ minWidth: 500 }}>Click a folder in the {this.props.entity} List to view record dashboard.</Callout>
                        </Card>
                    </div>
                </div>
            )
        } else {
            dashboard = (
                <WrcStatusDashboard
                    db_schema={this.props.db_schema}
                    table_schema={this.props.table_schema}
                    record_id={this.state.record_id}
                    table_name={this.props.table_name}
                    component_alignment={this.props.component_alignment}
                    component_index={this.props.component_index}
                    refresh={this.props.refresh}
                    parentSetState={this.props.parentSetState}
                    parentGetState={this.props.parentGetState}
                    parentCallback={this.parentCallback}
                    entity={this.props.entity}
                />
            )
        }

        let input_button = '';
        if (this.props.input_type === 'import') {
            input_button = (
                <WrcScheduleOpener
                    button_text={'Import ' + this.props.entity}
                    refresh={this.props.refresh}
                    parentSetState={this.props.parentSetState}
                    parentGetState={this.props.parentGetState}
                    parentCallback={this.parentCallback}
                    component_index={this.props.component_index}
                    page_length={this.props.page_length}
                    registry={this.props.registry}
                    import_conditions={this.props.import_conditions}
                />
            );
        } else if (!this.props.readonly && this.state.readonly_if !== true) {
            input_button = (
                <Button icon={IconNames.NEW_PERSON}
                    intent={Intent.PRIMARY}
                    text={'New ' + this.props.entity}
                    style={{ marginTop: '1em', marginBottom: '1em' }}
                    fill={false}
                    onClick={() => this.showForm()} />
            );
        }


        return (this.state.is_loaded ?
            local_list_data.length === 0 ?
                (<div className={ld_full_class}>
                    <div className={ld_list_class}>
                        <Card interactive={false} elevation={Elevation.TWO}>
                            <div style={{ "minWidth": "180px", "display": "flex", "flexDirection": "row", "justifyContent": "space-between", "alignItems": "baseline" }}>
                                {this.state.collapse_enabled &&
                                    <Button
                                        onClick={() => this.handleListShow()}
                                        icon={this.state.show_list ? "caret-down" : "caret-right"}
                                        small={true}
                                    />}
                                <WrcCardHeader small={true} title={this.props.entity + ' List'} />
                            </div>
                            <Collapse isOpen={this.state.collapse_enabled == false ? true : this.state.show_list} keepChildrenMounted={true}>
                                <Callout intent={Intent.WARNING} title='No records' style={{ marginBottom: 10, minWidth: 500 }}>No {this.props.entity} records have been created yet.</Callout>
                                <div>
                                    {input_button}
                                </div>
                            </Collapse>
                        </Card>
                    </div>
                </div>) :
                (<div className={ld_full_class}>
                    <div className={ld_list_class}>
                        <Card interactive={false} elevation={Elevation.TWO}>
                            <div style={{ "minWidth": "180px", "display": "flex", "flexDirection": "row", "justifyContent": "space-between", "alignItems": "baseline" }}>
                                {this.state.collapse_enabled &&
                                    <Button
                                        onClick={() => this.handleListShow()}
                                        icon={this.state.show_list ? "caret-down" : "caret-right"}
                                        small={true}
                                    />}
                                <WrcCardHeader small={true} title={this.props.entity + ' List'} />
                            </div>
                            <Collapse isOpen={this.state.collapse_enabled == false ? true : this.state.show_list} keepChildrenMounted={true}>
                                <WrcDataTable
                                    index={this.props.component_index}
                                    entity={this.props.entity}
                                    page_length={this.props.page_length}
                                    show_search={true}
                                    className={this.props.class_name}
                                    noWrapHeader={false}
                                    style={{ width: '100%' }}
                                >
                                    <thead>
                                        <tr>
                                            {list_headers}
                                            <th class="nosort">{this.props.readonly ? "" : (this.props.children ? "Add / " : "") + "Edit / "}View</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {local_list_data}
                                    </tbody>
                                </WrcDataTable>
                                <div>
                                    {input_button}
                                </div>
                            </Collapse>
                        </Card>
                    </div>
                    {dashboard}
                    <div className={ld_detail_class} id={'child-' + this.props.component_index}>
                        <Collapse isOpen={this.state.collapse_enabled == false ? true : this.state.show_list} keepChildrenMounted={true}>
                            {details}
                        </Collapse>
                    </div>
                </div>)
            : <div>Loading...</div>
        )
    };
}

export default WrcListAndDetail;
